import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Package, Users, ChefHat, ClipboardList, LayoutDashboard, LogOut,
  Menu, X, FileText, ShoppingCart, PackageCheck, UserCog, Building2,
  ArrowLeftRight, ChevronDown, ChevronRight, Briefcase, Settings,
  MapPin, BarChart3, DollarSign, CreditCard, Database, Shield,
  Heart, Warehouse, Truck, Factory, Monitor, Store, Wrench, PieChart,
  Upload,
  PanelLeftClose, PanelLeft, MoreHorizontal,
  Sun, CloudSun, Moon,
  Search, Plus, Clock, History
} from 'lucide-react';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { usePermissions } from '../context/PermissionsContext';
import { NotificationBell } from './NotificationCenter';
import { LowStockAlertBadge } from './LowStockAlerts';
import { BranchSelector } from './BranchSelector';
import { useBranch } from '../context/BranchContext';
import Breadcrumb from './Breadcrumb';
import KeyboardShortcuts from './KeyboardShortcuts';
import GlobalSearchModal from './GlobalSearchModal';
import ContextualActionBar from './ContextualActionBar';

const GROUP_COLORS = {
  sales: 'border-emerald-400',
  supply: 'border-blue-400',
  production: 'border-amber-400',
  finance: 'border-purple-400',
  hr: 'border-orange-400',
  reports: 'border-cyan-400',
  admin: 'border-red-400',
};

const SidebarNavGroups = React.memo(function SidebarNavGroups({ navGroups, canViewGroup, canViewItem, isActive, isGroupActive, toggleGroup, expandedGroups, isSidebarCollapsed, navCounts }) {
  return (
    <div className="mt-2 space-y-0.5">
      {navGroups.filter(g => canViewGroup(g)).map((group) => {
        const GroupIcon = group.icon;
        const isOpen = expandedGroups.includes(group.id);
        const groupActive = isGroupActive(group);
        const visibleItems = group.items.filter(item => canViewItem(item));
        const borderColor = GROUP_COLORS[group.id] || '';

        if (visibleItems.length === 1) {
          const item = visibleItems[0];
          const active = isActive(item.path);
          return (
            <Link
              key={group.id}
              to={item.path}
              aria-current={active ? 'page' : undefined}
              className={`${isSidebarCollapsed ? 'group/tip relative' : ''} flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                active
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              <GroupIcon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-emerald-400' : 'text-slate-500'}`} />
              {!isSidebarCollapsed && <span>{group.label}</span>}
              {!isSidebarCollapsed && active && <div className="ml-auto w-1.5 h-1.5 bg-emerald-400 rounded-full" />}
              {isSidebarCollapsed && (
                <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 z-[100] shadow-lg pointer-events-none">
                  {group.label}
                </span>
              )}
            </Link>
          );
        }

        if (isSidebarCollapsed) {
          return (
            <Link
              key={group.id}
              to={visibleItems[0].path}
              className={`group/tip relative flex items-center justify-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                groupActive
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              <GroupIcon className={`w-[18px] h-[18px] flex-shrink-0 ${groupActive ? 'text-emerald-400' : 'text-slate-500'}`} />
              <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 z-[100] shadow-lg pointer-events-none">
                {group.label}
              </span>
            </Link>
          );
        }

        return (
          <div key={group.id}>
            <button
              onClick={() => toggleGroup(group.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleGroup(group.id);
                }
              }}
              tabIndex={0}
              aria-expanded={isOpen}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                isOpen && borderColor ? `border-l-[3px] ${borderColor}` : 'border-l-[3px] border-transparent'
              } ${
                groupActive
                  ? 'text-emerald-400'
                  : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              <GroupIcon className={`w-[18px] h-[18px] flex-shrink-0 ${groupActive ? 'text-emerald-400' : 'text-slate-500'}`} />
              <span className="flex-1 text-left">{group.label}</span>
              {group.id === 'supply' && navCounts.inventory > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full font-medium">{navCounts.inventory > 999 ? '999+' : navCounts.inventory}</span>
              )}
              {group.id === 'sales' && navCounts.today_sales > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded-full font-medium">{navCounts.today_sales}</span>
              )}
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
            </button>
            {isOpen && (
              <div className="ml-4 pl-3 border-l border-white/10 mt-0.5 space-y-0.5">
                {visibleItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                        active
                          ? 'bg-emerald-500/15 text-emerald-400 font-medium border-l-2 border-emerald-400'
                          : 'text-slate-500 hover:bg-white/5 hover:text-slate-300 border-l-2 border-transparent'
                      }`}
                    >
                      <span className="flex-1">{item.label}</span>
                      {item.path === '/inventory' && navCounts.low_stock > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded-full font-medium" title={`${navCounts.low_stock} low stock`}>{navCounts.low_stock}</span>
                      )}
                      {item.path === '/purchase-orders' && navCounts.pending_pos > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full font-medium">{navCounts.pending_pos}</span>
                      )}
                      {item.path === '/hr/leave' && navCounts.pending_leave > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded-full font-medium">{navCounts.pending_leave > 999 ? '999+' : navCounts.pending_leave}</span>
                      )}
                      {active && <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

function getGreeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'Good Morning';
  if (h >= 12 && h < 17) return 'Good Afternoon';
  if (h >= 17 && h < 21) return 'Good Evening';
  return 'Good Night';
}

function Layout({ children, user, setUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('office_suite_sidebar_state') === 'collapsed';
    } catch { return false; }
  });
  const { can, isAdmin } = usePermissions();
  const { currentBranch } = useBranch();
  const [greeting, setGreeting] = useState(getGreeting());
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  const [recentItems, setRecentItems] = useState([]);
  const [showRecentItems, setShowRecentItems] = useState(false);
  const [navCounts, setNavCounts] = useState({});

  useEffect(() => {
    const interval = setInterval(() => {
      setGreeting(getGreeting());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = () => setIsProfileOpen(false);
    if (isProfileOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isProfileOpen]);

  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const lastActivity = useRef(Date.now());
  const timeoutTimer = useRef(null);

  useEffect(() => {
    const path = location.pathname;
    const expand = (id) => setExpandedGroups(prev => prev.includes(id) ? prev : [...prev, id]);
    if (path.startsWith('/sales') || path.startsWith('/pos')) expand('sales');
    else if (path.startsWith('/customers')) expand('customers');
    else if (path.startsWith('/inventory') || path.startsWith('/suppliers') || path.startsWith('/purchase-orders') || path.startsWith('/receiving') || path.startsWith('/stock-transfers'))
      expand('supply');
    else if (path.startsWith('/procurement')) expand('procurement');
    else if (path.startsWith('/warehouse')) expand('warehouse');
    else if (path.startsWith('/production') || path.startsWith('/recipes'))
      expand('production');
    else if (path.startsWith('/finance')) expand('finance');
    else if (path.startsWith('/hr')) expand('hr');
    else if (path.startsWith('/assets')) expand('assets');
    else if (path.startsWith('/store-operations')) expand('storeops');
    else if (path.startsWith('/reports') || path.startsWith('/branch-comparison') || path.startsWith('/advanced-analytics'))
      expand('reports');
  }, [location.pathname]);

  const resetActivityTimer = useCallback((refreshToken = false) => {
    lastActivity.current = Date.now();
    setShowTimeoutWarning(false);
    if (refreshToken) {
      const token = localStorage.getItem('token');
      if (token) {
        fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }).catch(() => {});
      }
    }
    if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
    timeoutTimer.current = setTimeout(() => {
      setShowTimeoutWarning(true);
    }, 25 * 60 * 1000);
  }, []);

  useEffect(() => {
    resetActivityTimer();
    let lastMouseMove = 0;
    const onMouseMove = () => {
      const now = Date.now();
      if (now - lastMouseMove < 1000) return;
      lastMouseMove = now;
      lastActivity.current = now;
    };
    const onKeypress = () => { lastActivity.current = Date.now(); };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('keypress', onKeypress);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('keypress', onKeypress);
      if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
    };
  }, [resetActivityTimer]);

  useEffect(() => {
    const loadRecent = () => {
      try {
        const stored = JSON.parse(localStorage.getItem('office_suite_recent_items') || '[]');
        setRecentItems(stored.slice(0, 10));
      } catch { setRecentItems([]); }
    };
    loadRecent();
    window.addEventListener('recentItemsUpdated', loadRecent);
    return () => window.removeEventListener('recentItemsUpdated', loadRecent);
  }, []);

  useEffect(() => {
    let lastFailed = false;
    let interval;
    const fetchCounts = () => {
      if (lastFailed) {
        lastFailed = false;
        return;
      }
      axios.get(`${API}/ux/nav-counts`)
        .then(res => { setNavCounts(res.data || {}); lastFailed = false; })
        .catch(() => { lastFailed = true; });
    };
    fetchCounts();
    interval = setInterval(fetchCounts, 120000);
    return () => clearInterval(interval);
  }, []);

  const isSearchOpenRef = useRef(isSearchOpen);
  const isQuickCreateOpenRef = useRef(isQuickCreateOpen);
  const navigateRef = useRef(navigate);
  useEffect(() => { isSearchOpenRef.current = isSearchOpen; }, [isSearchOpen]);
  useEffect(() => { isQuickCreateOpenRef.current = isQuickCreateOpen; }, [isQuickCreateOpen]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        navigateRef.current('/notifications');
      }
      if (e.key === 'Escape') {
        if (isSearchOpenRef.current) { setIsSearchOpen(false); return; }
        if (isQuickCreateOpenRef.current) { setIsQuickCreateOpen(false); return; }
        const closeBtn = document.querySelector('[role="dialog"] button[data-testid="close-dialog"], [role="dialog"] button[aria-label="Close"]');
        if (closeBtn) closeBtn.click();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  }, [setUser, navigate]);

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups(prev =>
      prev.includes(groupId) ? prev.filter(g => g !== groupId) : [...prev, groupId]
    );
  }, []);

  const navGroups = useMemo(() => [
    {
      id: 'sales',
      label: 'Sales & POS',
      icon: CreditCard,
      moduleKey: 'module_sales',
      items: [
        { path: '/sales', label: 'Sales Dashboard', icon: CreditCard, permission: null },
        { path: '/pos', label: 'POS Management', icon: Monitor, permission: null },
      ]
    },
    {
      id: 'customers',
      label: 'Customers & CRM',
      icon: Heart,
      items: [
        { path: '/customers', label: 'Customer Management', icon: Heart, permission: null },
        { path: '/sales', label: 'Sales Dashboard', icon: CreditCard, permission: 'sales_view' },
        { path: '/sales/reports', label: 'Sales Reports', icon: BarChart3, permission: 'sales_view' },
        { path: '/sales-advanced', label: 'Returns & Loyalty', icon: CreditCard, permission: 'sales_view' },
        { path: '/pos-improvements', label: 'POS Operations', icon: CreditCard, permission: 'sales_view' },
      ]
    },
    {
      id: 'supply',
      label: 'Inventory & Supply Chain',
      icon: Package,
      moduleKey: 'module_inventory',
      items: [
        { path: '/inventory', label: 'Inventory', icon: Package, permission: 'inventory_view' },
        { path: '/inventory-advanced', label: 'Advanced Inventory', icon: PackageCheck, permission: 'inventory_view' },
        { path: '/suppliers', label: 'Suppliers', icon: Users, permission: 'inventory_view' },
        { path: '/inventory-advanced', label: 'ABC & Cycle Counts', icon: Package, permission: 'inventory_view' },
        { path: '/inventory-improvements', label: 'Inventory Ops Center', icon: Package, permission: 'inventory_view' },
        { path: '/inventory-reports', label: 'Inventory Reports', icon: Package, permission: 'inventory_view' },
        { path: '/suppliers', label: 'Suppliers', icon: Users, permission: 'suppliers_view' },
        { path: '/purchase-orders', label: 'Purchase Orders', icon: ShoppingCart, permission: 'po_view' },
        { path: '/receiving', label: 'Receiving', icon: PackageCheck, permission: 'po_view' },
        { path: '/stock-transfers', label: 'Stock Transfers', icon: ArrowLeftRight, permission: 'transfer_view' },
      ]
    },
    {
      id: 'procurement',
      label: 'Procurement Hub',
      icon: ShoppingCart,
      items: [
        { path: '/procurement', label: 'Procurement', icon: ShoppingCart, permission: 'po_view' },
      ]
    },
    {
      id: 'warehouse',
      label: 'Warehouse & Logistics',
      icon: Truck,
      items: [
        { path: '/warehouse', label: 'Warehouse & Delivery', icon: Warehouse, permission: null },
      ]
    },
    {
      id: 'production',
      label: 'Production & Bakery',
      icon: ChefHat,
      moduleKey: 'module_production',
      items: [
        { path: '/production', label: 'Production', icon: ChefHat, permission: 'production_log' },
        { path: '/production/dashboard', label: 'Production Dashboard', icon: LayoutDashboard, permission: 'production_log' },
        { path: '/recipes', label: 'Recipes', icon: ClipboardList, permission: 'recipes_view' },
        { path: '/production/planning', label: 'Planning', icon: ClipboardList, permission: 'production_log' },
        { path: '/production/quality', label: 'Quality Control', icon: ClipboardList, permission: 'production_log' },
        { path: '/production-advanced', label: 'Advanced Production', icon: Factory, permission: 'production_log' },
        { path: '/production-advanced', label: 'Equipment & HACCP', icon: ChefHat, permission: 'production_log' },
        { path: '/production-improvements', label: 'Production Ops Center', icon: ChefHat, permission: 'production_log' },
      ]
    },
    {
      id: 'finance',
      label: 'Finance & Accounting',
      icon: DollarSign,
      moduleKey: 'module_finance',
      items: [
        { path: '/finance', label: 'Finance Dashboard', icon: DollarSign, permission: null },
        { path: '/finance-advanced', label: 'Advanced Finance', icon: DollarSign, permission: null },
        { path: '/finance/dashboard', label: 'Dashboard', icon: DollarSign, permission: 'finance_view' },
        { path: '/finance/invoices', label: 'Invoices', icon: FileText, permission: 'finance_view' },
        { path: '/finance/bills', label: 'Bills', icon: FileText, permission: 'finance_view' },
        { path: '/finance/expenses', label: 'Expenses', icon: CreditCard, permission: 'finance_view' },
        { path: '/finance/banking', label: 'Banking', icon: DollarSign, permission: 'finance_view' },
        { path: '/finance/chart-of-accounts', label: 'Chart of Accounts', icon: DollarSign, permission: 'finance_view' },
        { path: '/finance/journal-entries', label: 'Journal Entries', icon: FileText, permission: 'finance_view' },
        { path: '/finance/reports', label: 'Reports', icon: BarChart3, permission: 'finance_view' },
        { path: '/finance-improvements', label: 'Finance Ops Center', icon: DollarSign, permission: 'finance_view' },
      ]
    },
    {
      id: 'hr',
      label: 'Human Resources',
      icon: Briefcase,
      moduleKey: 'module_hr',
      items: [
        { path: '/hr', label: 'HR Dashboard', icon: Briefcase, permission: 'hr_view' },
        { path: '/hr/employees', label: 'Employees', icon: Users, permission: 'hr_employees_manage' },
        { path: '/hr/payroll', label: 'Payroll', icon: DollarSign, permission: 'hr_payroll_view' },
        { path: '/hr/attendance', label: 'Attendance', icon: ClipboardList, permission: 'hr_attendance_manage' },
        { path: '/hr/leave', label: 'Leave', icon: FileText, permission: 'hr_leave_manage' },
        { path: '/hr/recruitment', label: 'Recruitment', icon: UserCog, permission: 'hr_employees_manage' },
        { path: '/hr/performance', label: 'Performance', icon: BarChart3, permission: 'hr_performance_manage' },
        { path: '/hr-advanced', label: 'Overtime & Loans', icon: Briefcase, permission: 'hr_payroll_view' },
        { path: '/hr-improvements', label: 'HR Ops Center', icon: Briefcase, permission: 'hr_view' },
      ]
    },
    {
      id: 'assets',
      label: 'Asset Management',
      icon: Wrench,
      adminOnly: true,
      items: [
        { path: '/assets', label: 'Assets & Maintenance', icon: Wrench, permission: null },
      ]
    },
    {
      id: 'storeops',
      label: 'Store Operations',
      icon: Store,
      items: [
        { path: '/store-operations', label: 'Store Operations', icon: Store, permission: null },
      ]
    },
    {
      id: 'masterdata',
      label: 'Master Data',
      icon: Database,
      moduleKey: 'module_master_data',
      items: [
        { path: '/master-data', label: 'Master Data', icon: Database, permission: null },
      ]
    },
    {
      id: 'reports',
      label: 'Reports & Analytics',
      icon: BarChart3,
      moduleKey: 'module_reports',
      items: [
        { path: '/reports', label: 'Reports', icon: BarChart3, permission: 'reports_view' },
        { path: '/reports/comprehensive', label: 'Comprehensive', icon: FileText, permission: 'reports_view' },
        { path: '/branch-comparison', label: 'Branch Analytics', icon: Building2, permission: null },
        { path: '/advanced-analytics', label: 'Advanced Analytics', icon: PieChart, permission: 'reports_view' },
        { path: '/branch-comparison', label: 'Branch Analytics', icon: Building2, permission: 'reports_view' },
        { path: '/branch-improvements', label: 'Branch Ops Center', icon: Building2, permission: 'reports_view' },
      ]
    },
    {
      id: 'admin',
      label: 'Administration',
      icon: Shield,
      moduleKey: 'module_admin',
      items: [
        { path: '/users', label: 'User Management', icon: UserCog, permission: 'users_view' },
        { path: '/branches', label: 'Branches', icon: Building2, permission: 'branches_manage' },
        { path: '/system', label: 'System Admin', icon: Shield, permission: 'settings_manage' },
        { path: '/compliance', label: 'Compliance & QA', icon: Shield, permission: 'settings_view' },
        { path: '/data-import-export', label: 'Import / Export', icon: Upload, permission: 'data_import' },
        { path: '/settings', label: 'Settings', icon: Settings, permission: 'settings_view' },
        { path: '/system-improvements', label: 'System Ops Center', icon: Shield, permission: 'settings_manage' },
      ]
    },
  ], []);

  const breadcrumb = useMemo(() => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return { section: 'Dashboard', page: null };
    for (const group of navGroups) {
      const item = group.items.find(i => path === i.path || path.startsWith(i.path + '/'));
      if (item) return { section: group.label, page: item.label };
    }
    const segments = path.split('/').filter(Boolean);
    return { section: segments[0]?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Home', page: segments.length > 1 ? segments.slice(1).join(' / ').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null };
  }, [location.pathname, navGroups]);

  useEffect(() => {
    const path = location.pathname;
    const routeTitles = {
      '/': 'Home - Office Suite',
      '/dashboard': 'Dashboard - Office Suite',
      '/inventory': 'Inventory - Office Suite',
      '/sales': 'Sales - Office Suite',
      '/production': 'Production - Office Suite',
      '/finance': 'Finance - Office Suite',
      '/hr': 'HR Dashboard - Office Suite',
      '/hr/employees': 'Employees - Office Suite',
      '/hr/payroll': 'Payroll - Office Suite',
      '/hr/attendance': 'Attendance - Office Suite',
      '/hr/leave': 'Leave Management - Office Suite',
      '/hr/recruitment': 'Recruitment - Office Suite',
      '/hr/performance': 'Performance - Office Suite',
      '/purchase-orders': 'Purchase Orders - Office Suite',
      '/suppliers': 'Suppliers - Office Suite',
      '/receiving': 'Receiving - Office Suite',
      '/stock-transfers': 'Stock Transfers - Office Suite',
      '/recipes': 'Recipes - Office Suite',
      '/reports': 'Reports - Office Suite',
      '/users': 'User Management - Office Suite',
      '/branches': 'Branches - Office Suite',
      '/settings': 'Settings - Office Suite',
      '/system': 'System Admin - Office Suite',
      '/profile': 'Profile - Office Suite',
      '/notifications': 'Notifications - Office Suite',
    };
    const exactMatch = routeTitles[path];
    if (exactMatch) {
      document.title = exactMatch;
    } else {
      const prefixMatch = Object.keys(routeTitles).find(key => key !== '/' && path.startsWith(key + '/'));
      if (prefixMatch) {
        document.title = routeTitles[prefixMatch];
      } else if (breadcrumb.page) {
        document.title = `${breadcrumb.page} - Office Suite`;
      } else if (breadcrumb.section) {
        document.title = `${breadcrumb.section} - Office Suite`;
      } else {
        document.title = 'Office Suite';
      }
    }
  }, [location.pathname, breadcrumb]);

  const isActive = useCallback((path) => {
    if (path === '/' || path === '/dashboard') return location.pathname === '/' || location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }, [location.pathname]);

  const isGroupActive = useCallback((group) => {
    return group.items.some(item => isActive(item.path));
  }, [isActive]);

  const canViewItem = useCallback((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.permission && !can(item.permission)) return false;
    return true;
  }, [isAdmin, can]);

  const canViewGroup = useCallback((group) => {
    if (group.moduleKey && !can(group.moduleKey)) return false;
    if (group.adminOnly && !isAdmin) return false;
    return group.items.some(item => canViewItem(item));
  }, [isAdmin, can, canViewItem]);

  const firstName = useMemo(() => user?.full_name?.split(' ')[0] || '', [user?.full_name]);

  return (
    <div className="min-h-screen bg-slate-50">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-emerald-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:outline-none focus:shadow-lg">
        Skip to main content
      </a>
      <KeyboardShortcuts />
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0F172A] safe-area-inset">
        <div className="flex items-center justify-between px-4 h-14">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 -ml-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="text-center">
              <span className="font-bold text-white">Office Suite</span>
              <p className="text-[8px] text-slate-400 uppercase tracking-wider">Supermarket · Bakery · Restaurant</p>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            <button onClick={() => setIsSearchOpen(true)} className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors" aria-label="Search">
              <Search className="w-4 h-4" />
            </button>
            <LowStockAlertBadge />
            <NotificationBell />
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden ring-2 ring-white/20"
              >
                <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-bold text-xs">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              </button>
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                  <div className="p-4 border-b border-slate-100 bg-slate-50">
                    <p className="font-semibold text-slate-900 truncate text-sm">{user?.full_name}</p>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{user?.email}</p>
                    <span className="inline-block mt-2 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-md capitalize">
                      {user?.role?.replace('_', ' ')}
                    </span>
                  </div>
                  <Link to="/profile" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                    <UserCog className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-slate-100"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-50 backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        role="navigation"
        aria-label="Main navigation"
        className={`
        fixed top-0 left-0 h-full z-50
        ${isSidebarCollapsed ? 'lg:w-[72px]' : 'lg:w-64'} w-[280px] bg-[#0F172A]
        transform transition-transform duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]
        lg:translate-x-0
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          <div className={`flex items-center justify-between ${isSidebarCollapsed ? 'px-3' : 'px-5'} h-16 border-b border-white/10`}>
            <Link to="/" className="flex items-center space-x-3">
              <img src="/logo.png" alt="Office Suite" className="w-9 h-9 rounded-lg flex-shrink-0 object-contain" />
              {!isSidebarCollapsed && (
                <div>
                  <span className="font-bold text-white text-lg">Office Suite</span>
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest">Supermarket · Bakery · Restaurant</p>
                </div>
              )}
            </Link>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {!isSidebarCollapsed && (
            <div className="px-3 py-3">
              <BranchSelector />
            </div>
          )}

          {!isSidebarCollapsed && (
            <div className="px-3 pb-2">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 text-sm transition-colors border border-white/5"
              >
                <Search className="w-4 h-4" />
                <span className="flex-1 text-left">Search...</span>
                <kbd className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded text-slate-500">⌘K</kbd>
              </button>
            </div>
          )}

          <nav className="flex-1 overflow-y-auto px-3 pb-4">
            <Link
              to="/"
              aria-current={location.pathname === '/' ? 'page' : undefined}
              className={`${isSidebarCollapsed ? 'group/tip relative' : ''} flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 mb-1 ${
                location.pathname === '/'
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              <LayoutDashboard className={`w-[18px] h-[18px] flex-shrink-0 ${location.pathname === '/' ? 'text-emerald-400' : 'text-slate-500'}`} />
              {!isSidebarCollapsed && <span>Home</span>}
              {isSidebarCollapsed && (
                <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 z-[100] shadow-lg pointer-events-none">Home</span>
              )}
            </Link>

            <Link
              to="/dashboard"
              aria-current={location.pathname === '/dashboard' ? 'page' : undefined}
              className={`${isSidebarCollapsed ? 'group/tip relative' : ''} flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 mb-1 ${
                location.pathname === '/dashboard'
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              <BarChart3 className={`w-[18px] h-[18px] flex-shrink-0 ${location.pathname === '/dashboard' ? 'text-emerald-400' : 'text-slate-500'}`} />
              {!isSidebarCollapsed && <span>Dashboard</span>}
              {isSidebarCollapsed && (
                <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 z-[100] shadow-lg pointer-events-none">Dashboard</span>
              )}
            </Link>

            <SidebarNavGroups
              navGroups={navGroups}
              canViewGroup={canViewGroup}
              canViewItem={canViewItem}
              isActive={isActive}
              isGroupActive={isGroupActive}
              toggleGroup={toggleGroup}
              expandedGroups={expandedGroups}
              isSidebarCollapsed={isSidebarCollapsed}
              navCounts={navCounts}
            />
          </nav>

          {!isSidebarCollapsed && recentItems.length > 0 && (
            <div className="px-3 pb-2 border-t border-white/10 pt-2">
              <button
                onClick={() => setShowRecentItems(!showRecentItems)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                <Clock className="w-3.5 h-3.5" />
                <span className="flex-1 text-left font-medium">Recent</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${showRecentItems ? '' : '-rotate-90'}`} />
              </button>
              {showRecentItems && (
                <div className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                  {recentItems.slice(0, 5).map((item, idx) => {
                    const typeIcons = { inventory: Package, order: ShoppingCart, supplier: Users, employee: Briefcase, purchase_order: ClipboardList, recipe: ChefHat };
                    const TypeIcon = typeIcons[item.type] || Package;
                    return (
                      <Link
                        key={`${item.type}-${item.id}-${idx}`}
                        to={`${item.path || '/'}?search=${encodeURIComponent(item.name)}`}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-white/5 hover:text-slate-300 transition-colors"
                      >
                        <TypeIcon className="w-3 h-3 flex-shrink-0" />
                        <span className="flex-1 truncate">{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="p-3 border-t border-white/10">
            {!isSidebarCollapsed && (
              <div className="hidden lg:flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2">
                  <LowStockAlertBadge />
                  <NotificationBell />
                </div>
              </div>
            )}
            {isSidebarCollapsed ? (
              <Link
                to="/profile"
                title={user?.full_name || 'Profile'}
                className="flex items-center justify-center p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-bold text-sm rounded-lg">
                    {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                </div>
              </Link>
            ) : (
              <Link
                to="/profile"
                className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-bold text-sm rounded-lg">
                    {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white text-sm truncate">{user?.full_name}</p>
                  <p className="text-xs text-slate-400 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
              </Link>
            )}
            <button
              onClick={handleLogout}
              title={isSidebarCollapsed ? 'Sign Out' : undefined}
              aria-label="Sign out"
              className={`mt-1.5 w-full flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-center gap-2'} p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-sm`}
            >
              <LogOut className="w-4 h-4" />
              {!isSidebarCollapsed && <span>Sign Out</span>}
            </button>
            <button
              onClick={() => {
                const next = !isSidebarCollapsed;
                setIsSidebarCollapsed(next);
                try { localStorage.setItem('office_suite_sidebar_state', next ? 'collapsed' : 'expanded'); } catch {}
              }}
              className="hidden lg:flex mt-1.5 w-full items-center justify-center gap-2 p-2 text-slate-500 hover:text-slate-300 hover:bg-white/5 rounded-lg transition-colors text-sm"
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
              {!isSidebarCollapsed && <span>Collapse</span>}
            </button>
            {!isSidebarCollapsed && (
              <p className="mt-2 pt-2 border-t border-white/5 text-[10px] text-slate-600 text-center">Powered by Office Suite v3.0</p>
            )}
          </div>
        </div>
      </aside>

      {isOffline && (
        <div className="fixed top-0 left-0 right-0 z-[61] bg-slate-700 text-white text-center py-2 text-sm font-medium flex items-center justify-center gap-2">
          <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
          You are currently offline. Some features may be unavailable.
        </div>
      )}

      {showTimeoutWarning && (
        <div
          className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white text-center py-2 text-sm font-medium cursor-pointer hover:bg-amber-600 transition-colors"
          onClick={() => resetActivityTimer(true)}
        >
          Your session will expire soon. Click to stay logged in.
        </div>
      )}

      <div role="status" aria-live="polite" className="sr-only" id="aria-live-region"></div>
      <main id="main-content" role="main" className={`${isSidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64'} pt-14 lg:pt-0 min-h-screen transition-all duration-300`}>
        <div className="bg-[#0F172A] border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-2">
            <div className="flex items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-300 inline-flex items-center gap-1.5">
                  {(() => {
                    const h = new Date().getHours();
                    if (h >= 5 && h < 12) return <Sun className="w-3.5 h-3.5 text-amber-400" />;
                    if (h >= 12 && h < 17) return <CloudSun className="w-3.5 h-3.5 text-amber-400" />;
                    return <Moon className="w-3.5 h-3.5 text-indigo-400" />;
                  })()}
                  {greeting}, {firstName}
                </span>
                {currentBranch && (
                  <>
                    <span className="text-slate-600">·</span>
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full text-xs font-medium">{currentBranch.name}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <button
                    onClick={() => setIsQuickCreateOpen(!isQuickCreateOpen)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors text-xs font-medium"
                    title="Quick Create"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">New</span>
                  </button>
                  {isQuickCreateOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsQuickCreateOpen(false)} />
                      <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50 py-1">
                        {[
                          { label: 'New Sale', icon: ShoppingCart, path: '/sales?action=new', color: 'text-blue-600' },
                          { label: 'New Inventory Item', icon: Package, path: '/inventory?action=add', color: 'text-emerald-600' },
                          { label: 'New Purchase Order', icon: ClipboardList, path: '/purchase-orders?action=add', color: 'text-cyan-600' },
                          { label: 'New Recipe', icon: ChefHat, path: '/recipes?action=add', color: 'text-amber-600' },
                          { label: 'New Employee', icon: Users, path: '/hr/employees?action=add', color: 'text-orange-600' },
                        ].map((item) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={item.path}
                              onClick={() => { navigate(item.path); setIsQuickCreateOpen(false); }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
                            >
                              <Icon className={`w-4 h-4 ${item.color}`} />
                              <span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
                <span className="text-xs text-slate-500 hidden md:inline">
                  {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Breadcrumb />
        <div key={location.pathname} className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto page-transition">
          {children}
        </div>
      </main>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#0F172A] border-t border-white/10 z-40 safe-area-inset-bottom">
        <div className="flex items-center justify-around h-16 px-1">
          {[
            { path: '/', icon: LayoutDashboard, label: 'Home' },
            { path: '/sales', icon: CreditCard, label: 'Sales' },
            { path: '/inventory', icon: Package, label: 'Inventory', permission: 'inventory_view' },
            { path: '/production', icon: ChefHat, label: 'Production', permission: 'production_log' },
          ].filter(item => {
            if (item.permission && !can(item.permission)) return false;
            return true;
          }).map((item) => {
            const Icon = item.icon;
            const active = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-col items-center justify-center min-h-[44px] min-w-[44px] px-3 py-1.5 rounded-lg transition-all duration-200 relative ${
                  active ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {active && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-emerald-400 rounded-full" />}
                <div className={`${active ? 'bg-emerald-500/15 px-3 py-0.5 rounded-full' : ''}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`text-[10px] mt-1 ${active ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
              </Link>
            );
          })}
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="flex flex-col items-center justify-center min-h-[44px] min-w-[44px] px-3 py-1.5 rounded-lg transition-all duration-200 text-slate-500 hover:text-slate-300"
            aria-label="More navigation options"
          >
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-[10px] font-medium mt-1">More</span>
          </button>
        </div>
      </nav>

      <div className="lg:hidden h-16" />

      {isSearchOpen && <GlobalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />}
      {(location.pathname.startsWith('/sales') || location.pathname.startsWith('/inventory') || location.pathname.startsWith('/production') || location.pathname.startsWith('/hr') || location.pathname.startsWith('/finance') || location.pathname.startsWith('/purchase-orders') || location.pathname.startsWith('/suppliers') || location.pathname.startsWith('/recipes')) && <ContextualActionBar />}
    </div>
  );
}

export default Layout;
